<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import moment from "moment-timezone";

import { ModalName } from '@/constants/modals';
import { getFormattedHour } from "@/helpers/date";
import { compareWorkersByWorkerMissionStatus } from "@/helpers/mission";
import { formatFullName, formatPerson } from "@/helpers/name";
import { capitalize, capitalizeAllString, concat } from "@/helpers/string";
import { useAuthStore } from "@/modules/auth/store/authStore";
import { useConfigurationStore } from "@/store/configurationStore";
import { useMissionActionsStore } from "@/store/missionActionsStore";
import { EContractType } from '@/types/contract';
import { type IMissionDB, MissionCommand, type MissionPositionFront, type WorkerMission, WorkerMissionStatus } from '@/types/mission';

import type { LabelValue } from '@/types/common';
import type { IJob, JobExperience } from '@/types/job';

import Badge from "@/dsComponents/Badge/Badge.vue";
import Button from "@/dsComponents/buttons/Button.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownGenerator from "@/dsComponents/Dropdown/DropdownGenerator.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";

import WorkerAvailableCommands from "@/components/Mission/WorkerAvailableCommands.vue";
import Tile from "@/components/tileContent/Tile.vue";

type Position = {
    name: string;
    jobId?: string;
    quantity: number;
    workers: WorkerMission[];
    experience: JobExperience | null;
    description?: string;
    justification?: string;
    hiringReason?: string;
    contractType?: string;
};

interface PopulatedMission extends IMissionDB {
    jobs: IJob[];
    availableCommands: MissionCommand[];
}

interface Props {
    mission: PopulatedMission;
    mode?: string;
    noCommands?: boolean;
    noClick?: boolean;
    noOpen?: boolean;
    status?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    mode: "",
    noCommands: false,
    noClick: false,
    noOpen: false,
    status: false,
});

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const emit = defineEmits<(e: "command", result: any) => void>();

const open = ref(false);

// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const route = useRoute();
const { t } = useI18n();
const modalStore = useModalStore();
const missionActionsStore = useMissionActionsStore();
const authStore = useAuthStore();
const configStore = useConfigurationStore();

const jobOptions = computed<LabelValue[]>(() => configStore.jobOptions);
const isAgency = computed<boolean>(() => authStore.isAgency ?? false);
const isCompany = computed<boolean>(() => authStore.isCompany ?? false);
const userContext = computed<string>(() => authStore.userContext ?? "agency");

const icons: Record<string, string> = {
    placed: "placed",
    presented: "presented",
    validated: "validated",
    proposed: "proposed",
    reproposed: "proposed",
    reproposedBySms: "proposed",
    accepted: "accepted",
    declined: "declined",
    confirmed: "confirmed",
    notconfirmed: "notconfirmed",
    rejected: "rejected",
    rejectedByAgency: "rejectedByAgency",
    rejectedByCompany: "rejectedByCompany",
    aborted: "aborted",
    canceled: "canceled",
    lost: "lost",
};

const workerStatusKey: Record<WorkerMissionStatus, string> = {
    [WorkerMissionStatus.PROPOSED]: "proposed",
    [WorkerMissionStatus.REPROPOSED]: "reproposed",
    [WorkerMissionStatus.REPROPOSED_BY_SMS]: "reproposedBySms",
    [WorkerMissionStatus.ACCEPTED]: "accepted",
    [WorkerMissionStatus.CONFIRMED]: "confirmed",
    [WorkerMissionStatus.DECLINED]: "declined",
    [WorkerMissionStatus.REJECTED_BY_AGENCY]: "rejectedByAgency",
    [WorkerMissionStatus.ABORTED]: "aborted",
    [WorkerMissionStatus.NOT_CONFIRMED]: "notconfirmed",
};

// Computed properties
const missionName = computed(() => {
    if (isAgency.value) {
        if (!props.mission.name && props.mission?.client?.name) {
            return props.mission.client.name;
        }
        return props.mission.name;
    }
    if (isCompany.value) {
        if (!props.mission.agencyId) {
            return t("page.order.newOrder");
        }
        return props.mission.agency?.name || t("page.order.newOrder");
    }
    return t("page.order.newOrder");
});

const hasWorkerConfirmed = computed(() => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return !!props.mission.workers?.filter((worker: any) => worker.status === WorkerMissionStatus.CONFIRMED).length;
});

const workerToConfirm = computed(() => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return props.mission.workers?.filter((worker: any) => worker.status === WorkerMissionStatus.ACCEPTED).length;
});
const workerToReplace = computed(() => {
    return props.mission.workers?.filter((worker) => [WorkerMissionStatus.REJECTED_BY_AGENCY, WorkerMissionStatus.ABORTED].includes(worker.status))
        .length;
});

const hasPeriods = computed(() => {
    return props.mission.periods?.length;
});

const hasPositions = computed(() => {
    return props.mission.positions?.length;
});

const alerts = computed(() => {
    return props.mission.alerts || [];
});

const isGanttWorkers = computed(() => {
    return props.mode === "ganttWorkers";
});

const isGanttClient = computed(() => {
    return props.mode === "ganttClient";
});

const isCompanyGanttWorkers = computed(() => {
    return isCompany.value && isGanttWorkers.value;
});

// More computed properties
const showAlerts = computed(() => {
    return !isCompanyGanttWorkers.value;
});

const hasSendToEvolia = computed(() => {
    return props.mission.sendToEvoliaTimestamp;
});

const positions = computed<Position[]>(() => {
    if (!props.mission.positions?.length || !props.mission.workers?.length) {
        return [];
    }

    return props.mission.positions
        .map((position: MissionPositionFront, index: number) => {
            const job = getJob(position.jobId);

            if (!job?.label) return null;

            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            const positionWorkers = props.mission
                // biome-ignore lint/suspicious/noExplicitAny: Init Biome
                .workers!.filter((w: any) => w.positionIndex === index)
                // biome-ignore lint/suspicious/noExplicitAny: Init Biome
                .map((w: any) => ({
                    ...w,
                    fullname: formatFullName(w),
                                                    status: w.status || WorkerMissionStatus.CONFIRMED,
                }))
                .sort(compareWorkersByWorkerMissionStatus);

            return {
                ...position,
                name: job.label,
                workers: positionWorkers,
            };
        })
        .filter((position) => position !== null);
});

const labelApplication = computed(() => {
    const candidateCount = props.mission.jobOffer?.candidates?.length;

    if (candidateCount) {
        return t("page.mission.applicationsToTreat", {
            number: candidateCount,
            plural: candidateCount > 1 ? "s" : "",
        });
    }
    return t("page.mission.noApplication");
});

const createdBy = computed(() => {
    const owner = formatPerson(props.mission.owner ?? props.mission.ownerAgency);
    const by = owner && concat([t("global.by"), owner]);
    if (props.mission.createdAt && props.mission.createdAt > 1 && by) {
        return concat([
            props.mission.extendsOf ? t("global.extendAt") : t("global.createdAt"),
            moment(props.mission.createdAt).format(t("date.shortFormat")),
            by,
        ]);
    }
    return null;
});

const getJob = (id: string | undefined) => {
    if (!id) return null;
    return jobOptions.value.find((i: LabelValue) => i.value === id);
};

const getJobLabel = (id: string): string | undefined => {
    const job = getJob(id);
    return job?.label;
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const emitCommand = (result: any) => {
    emit("command", result);
};

const handleShowMissionDetails = () => {
    if (props.noClick) {
        return;
    }
    modalStore.actions.open(ModalName.MissionDetails, { mission: props.mission });
};

const toggle = () => {
    open.value = !open.value;
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const formatPeriod = (period: any) => {
    if (!period) return "";

    const periodStart = moment(period.start);
    const periodEnd = moment(period.end);

    if (periodStart.month() === periodEnd.month() && periodStart.year() === periodEnd.year()) {
        return `${periodStart.format("DD")} - ${periodEnd.format("DD MMM YYYY")}`;
    } else if (periodStart.year() === periodEnd.year()) {
        return `${periodStart.format("DD MMM")} - ${periodEnd.format("DD MMM YYYY")}`;
    } else {
        return `${periodStart.format("DD MMM YYYY")} - ${periodEnd.format("DD MMM YYYY")}`;
    }
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const missionNotStarted = (mission: any) => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    if (mission.periods && mission.periods.length) {
        const now = moment().startOf("day").valueOf();
        return now > mission.periods[0].start;
    }
    return false;
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const alertLabel = (alert: any) => {
    if (alert.kind === "missingWorker") {
        if (alert.data.count === 0) {
            return t("mission.alerts.missingWorker.zero", { total: alert.data?.total || 0 });
        }
        return t("mission.alerts.missingWorker.more", { nb: alert.data?.count || 0, total: alert.data?.total || 0 });
    }
    if (alert.kind === "missionNotCompleted") {
        return t("mission.alerts.missionNotCompleted");
    }
    if (alert.kind === "missingEvaluation") {
        if (alert.data?.who && alert.data?.who !== "both") {
            return t(`mission.alerts.missingEvaluation.${alert.data?.who}`);
        }
        return undefined;
    }
    if (alert.kind === "replaceWorkers") {
        return t("mission.alerts.replaceWorkers", { total: alert.data.count, plural: (alert.data.count > 1 && "s") || "" });
    }
    if (alert.kind === "workersToConfirmed") {
        return t("mission.alerts.workersToConfirmed", { total: alert.data.toConfirmed, plural: (alert.data.toConfirmed > 1 && "s") || "" });
    }
    if (alert.kind === "workersToValidate") {
        return t("mission.alerts.workersToValidate", { total: alert.data.toValidate, plural: (alert.data.toValidate > 1 && "s") || "" });
    }
    if (alert.kind === "workersToEvaluate") {
        return t("mission.alerts.workersToEvaluate", { total: alert.data.toEvaluate, plural: (alert.data.toEvaluate > 1 && "s") || "" });
    }
    if (alert.kind === "addWorkers") {
        return t("mission.alerts.addWorkers");
    }
    if (alert.kind === "companyAsk") {
        return t("mission.alerts.companyAsk");
    }
    if (alert.kind === "companyAskQuote") {
        return t("mission.alerts.companyAskQuote");
    }
    if (alert.kind === "selectAgency") {
        return t("mission.alerts.selectAgency");
    }

    const fullname = formatFullName(alert.data.worker);
    if (alert.status === "aborted" || alert.kind === "workerAborted") {
        return t("mission.alerts.status.aborted", { fullname });
    }
    if (alert.status === "validated" || alert.kind === "workerValidated") {
        return t("mission.alerts.status.validated", {
            clientname: alert.data.clientName,
            fullname,
        });
    }
    if (alert.status === "accepted" || alert.kind === "workerAccepted") {
        return t("mission.alerts.status.accepted", { fullname });
    }
    if (alert.status === "declined" || alert.kind === "workerDeclined") {
        return t("mission.alerts.status.declined", { fullname });
    }
    if (alert.status === "rejectedByCompany" || alert.kind === "workerRejectedByCompany") {
        return t("mission.alerts.status.rejectedByCompany", {
            clientname: alert.data.clientName,
            fullname,
        });
    }
};

const getPositionNumbers = (position: MissionPositionFront) => {
    const total = position.quantity;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const confirmed = props.mission.workers?.filter((w: any) => w.status === WorkerMissionStatus.CONFIRMED).length;

    return `<div class="nb-workers"><span>${confirmed}/${total}</span></div>`;
};

const setActions = () => {
    return {
        relatedData: { missionId: props.mission.id, mission: props.mission },
        actions: missionActionsStore.getMissionActions([...(props.mission.availableCommands as MissionCommand[]), MissionCommand.show]),
    };
};
</script>

<template>
    <div>
        <div :class="['MissionTile', mode]">
            <Tile @click="handleShowMissionDetails">
                <div class="section head" v-if="!isCompanyGanttWorkers">
                    <div class="top-head">
                        <div class="name ellipsis" :title="missionName" @click="handleShowMissionDetails">
                            <span>{{ missionName }}</span>
                        </div>
                        <div>
                            <Dropdown v-if="!noCommands" width="200px" close-on-descendant stop>
                                <template #trigger-zone>
                                    <Button iconOnly="dots" size="xs" type="neutral" variant="ghost" />
                                </template>
                                <template #dropdown-content>
                                    <DropdownGenerator :actions="setActions().actions" :relatedData="setActions().relatedData" />
                                </template>
                            </Dropdown>
                        </div>
                    </div>
                    <Badge icon="database" size="sm" :label="$t('global.evolia')" backgroundColor="transparent" v-if="hasSendToEvolia" />
                </div>
                <div class="section head" v-else @click="handleShowMissionDetails">
                    <div class="name ellipsis" :title="missionName">
                        <i class="ti ti-work" />
                        <span v-if="mission.job">{{ mission.job.name }}</span>
                    </div>
                    <div>
                        <Dropdown v-if="!noCommands" width="200px" close-on-descendant stop>
                            <template #trigger-zone>
                                <Button iconOnly="dots" size="xs" type="neutral" variant="ghost" />
                            </template>
                            <template #dropdown-content>
                                <DropdownGenerator :actions="setActions().actions" :relatedData="setActions().relatedData" />
                            </template>
                        </Dropdown>
                    </div>
                </div>
                <div class="section jobs-section" v-if="hasPositions && isAgency" @click="handleShowMissionDetails">
                    <i class="ti ti-work" />
                    <div class="job" v-for="(position, index) in mission.positions" :key="index">
                        <span class="job-label ellipsis" :title="getJobLabel(position.jobId)">{{ getJobLabel(position.jobId) }}</span>
                        <span class="contract-type">
                            {{
                                position.contractType && position.contractType !== EContractType.NONE
                                    // biome-ignore lint/style/useTemplate: Init Biome
                                    ? "(" + $t("mission.job.contractType.details." + position.contractType) + ")"
                                    : ""
                            }}
                        </span>
                        <div class="job-state" v-html="getPositionNumbers(position)" />
                    </div>
                </div>
                <div class="section periods" v-if="hasPeriods" @click="handleShowMissionDetails">
                    <div :class="['ti', 'ti-cal-exclamation', missionNotStarted(mission) ? 'has-started' : '']"></div>
                    <div class="periods-wrapper">
                        <div class="period" v-for="(period, index) in mission.periods" :key="`p${index}`">
                            <span :class="missionNotStarted(mission) ? 'has-started' : ''" v-html="formatPeriod(period)" />
                            <span v-if="Object.keys(period.slots).length > 1">
                                {{ $t("mission.multiplePeriods") }}
                            </span>
                            <span v-else-if="period.slots?.all && period.slots.all.length === 1">
                                {{ $t("global.from2") }} {{ getFormattedHour(period.slots.all[0].from) }} {{ $t("global.to2") }}
                                {{ getFormattedHour(period.slots.all[0].to) }}
                            </span>
                            <span v-else>
                                {{ $t("mission.multiplePeriods") }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="section address-section" v-if="mission.address" @click="handleShowMissionDetails">
                    <icon class="grey" icon="mapMarker2" />
                    <div :title="mission.address.city">{{ mission.address.city }}</div>
                </div>
                <div class="section" v-if="workerToConfirm && hasWorkerConfirmed === false">
                    <Badge color="green" :label="$t('page.interim.planning.workerToConfirm', { count: workerToConfirm })" size="sm" />
                </div>
                <div class="section" v-if="workerToReplace && hasWorkerConfirmed === false">
                    <Badge color="red" :label="$t('page.interim.planning.workerToReplace', { count: workerToReplace })" size="sm" />
                </div>
                <div class="section application-to-treat" v-if="mission.jobOffer" @click="handleShowMissionDetails">
                    <Badge icon="megaphone" size="sm" color="transparent" :label="labelApplication" />
                </div>
                <template v-if="showAlerts">
                    <div class="section alert-container" v-if="alerts?.length" @click="handleShowMissionDetails">
                        <div
                            :class="['bottom-head', 'alert', alert.level]"
                            v-for="(alert, index) in alerts"
                            :key="`alert${index}`"
                            :title="alertLabel(alert)"
                        >
                            {{ alertLabel(alert) }}
                        </div>
                    </div>
                </template>

                <div class="bottom">
                    <div class="bottom-head">
                        <span>{{ $t(`mission.status.${mission.status}.agency`) }}</span>
                    </div>
                    <div class="bottom-head">
                        <span :title="createdBy" v-if="createdBy">{{ createdBy }}</span>
                        <span v-else :title="$t(`mission.status.${mission.status}`)">
                            {{ $t(`mission.status.${mission.status}.${userContext}`) }}
                        </span>
                        <icon @click.stop="toggle" icon="triangle" v-if="!noOpen && positions.length > 0" :class="{ open }" />
                    </div>
                    <collapse-transition mode="out-in">
                        <div v-if="open">
                            <div v-if="!isGanttClient" class="bottom-content">
                                <div v-for="(position, index) in positions" :key="`position${index}`">
                                    <div class="position">
                                        <div class="position-name" v-if="positions.length > 1">{{ position.name }}</div>
                                        <div class="position-workers">
                                            <div
                                                :class="['position-worker', { 'without-commands': isCompany }]"
                                                v-for="(worker, index2) in position.workers"
                                                :key="`worker${index2}`"
                                            >
                                                <div class="name" :title="formatFullName(worker)">
                                                    <div>{{ capitalizeAllString(worker.lastname) }}</div>
                                                    <div>{{ worker.firstname }}</div>
                                                </div>
                                                <div class="icons">
                                                    <icon
                                                        class="grey"
                                                        icon="phonetel"
                                                        v-if="worker.registered"
                                                        :title="t(`mission.workerStatus.registered.${userContext}`)"
                                                    />
                                                    <icon :icon="`workerStatus_${workerStatusKey[worker.status]}`" />
                                                    <WorkerAvailableCommands
                                                        v-if="isAgency"
                                                        :mission="mission"
                                                        :worker="worker"
                                                        @command="emitCommand"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="bottom-content">
                                <div v-for="(job, indexJob) in mission.jobs" :key="indexJob">
                                    <div class="position">
                                        <div v-if="mission.jobs.length > 1" class="position-name" :title="job.name">{{ job.name }}</div>
                                        <div class="position-workers">
                                            <div
                                                :class="['position-worker', { 'without-commands': isCompany }]"
                                                v-for="(worker, index2) in mission.workers"
                                                :key="`worker${index2}`"
                                            >
                                                <div
                                                    v-if="worker.positionIndex === indexJob"
                                                    :class="['position-worker', { 'without-commands': isCompany }]"
                                                >
                                                    <div class="name" :title="formatFullName(worker)">
                                                        <div>{{ capitalize(worker.firstname) }}</div>
                                                        <div>{{ worker.lastname }}</div>
                                                    </div>
                                                    <div class="icons">
                                                        <icon
                                                            class="grey"
                                                            icon="phonetel"
                                                            v-if="worker.registered"
                                                            :title="$t(`mission.workerStatus.registered.${userContext}`)"
                                                        />
                                                        <span
                                                            v-if="worker.status"
                                                            :title="$t(`mission.workerStatus.${workerStatusKey[worker.status]}.${userContext}`)"
                                                        >
                                                            <icon :icon="`workerStatus_${icons[workerStatusKey[worker.status]]}`" />
                                                        </span>
                                                        <worker-available-commands
                                                            v-if="isAgency"
                                                            :mission="mission"
                                                            :worker="worker"
                                                            @command="emitCommand"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </collapse-transition>
                </div>
            </Tile>
        </div>
    </div>
</template>
<style scoped lang="scss">
.MissionTile {
    cursor: pointer;

    .bottom {
        &:disabled {
            cursor: default;
        }
    }

    .ellipsis {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .head {
        .top-head {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            .name {
                font-size: 18px;
                color: $black;
                font-weight: $medium;
            }
        }

        .ds-badge-component {
            padding: 0;
        }
    }

    &.month .Tile {
        margin-bottom: calc($padding / 2) !important;
    }

    .section {
        display: flex;
        gap: 8px;
        font-size: 12px;
        .ti {
            font-size: 14px;
        }
    }

    .jobs-section {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .job {
        display: flex;
        align-content: center;
        max-width: calc(100% - (14px + 8px));
        gap: 4px;
        align-items: center;
        justify-content: space-between;

        .job-name {
            color: $black;
        }

        .job-label {
            flex-shrink: 2;
            min-width: 0;
            max-width: 50%;
        }

        .contract-type {
            flex-shrink: 0;
        }

        .job-state {
            color: $black;
            flex-shrink: 0;
            .nb-workers {
                &.blue {
                    span:first-child {
                        color: $teal;
                    }
                }
                &.red {
                    span:first-child {
                        color: $red;
                    }
                }
                &.orange {
                    span:first-child {
                        color: $orange;
                    }
                }
            }
        }
    }

    .position {
        &-workers {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        &-worker {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 4px;

            .name {
                font-size: 11px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .icons {
                display: flex;
                align-items: center;
                gap: 4px;
            }

            &.without-commands {
                .name {
                    width: calc(100% - 41px);
                }
            }
        }
    }

    .bottom-content {
        padding: 0 8px;
        .action {
            text-align: center;

            .Action {
                border-radius: 20px;
                padding: 0px 10px;
                height: 28px;
                font-size: 12px;
                background-color: #5e72eb;
                color: white;
            }
        }
    }

    .alert-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .bottom-head {
        & > span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 14px;
        }

        &.info {
            background: $lightgreen !important;
            color: #2f9326;
        }

        &.todo {
            background: $lightorange !important;
            color: #8c5117;
        }

        &.warning {
            background: $lightred !important;
            color: #871414;
        }

        &.alert {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            display: block !important;
            line-height: 26px;
            border-radius: 20px;
            text-align: center;
            padding: 0px 10px;
            font-size: 12px;
        }
    }

    .Icon.triangle {
        transition: transform 0.3s ease-in-out;
        &.open {
            transform: rotate(180deg);
        }
    }

    .periods {
        display: flex;

        .period {
            margin-bottom: calc($padding / 4);

            &:last-child {
                margin-bottom: 0;
            }
        }
        .periods-wrapper > .period {
            display: flex;
            flex-direction: column;
            font-size: 10px;
            font-weight: $light;

            & > span:first-child {
                font-size: 12px;
                margin-top: 1px;
                font-weight: $regular;
            }

            & > span:last-child {
                margin-top: 2px;
            }
        }

        &.hours {
            .period span:first-child {
                font-weight: $light;
            }
        }

        .has-started {
            color: $red-base;
        }
    }

    .address-section {
        display: flex;
        align-items: center;
    }
}
</style>
